import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Package, CheckCircle, XCircle, AlertCircle, Loader, RefreshCcw, History, Clock, CheckSquare, Square } from 'lucide-react';
import { 
  getPendingHodApprovals, 
  approveAssetDeclaration,
  bulkApproveAssetDeclarations,
  getHodApprovalHistory
} from '../../../models/assetsModel';

export default function AssetHodApprovalsPage() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState('pending'); // 'pending', 'history'
  const [declarations, setDeclarations] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [comments, setComments] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkComment, setBulkComment] = useState('');
  const currentYear = new Date().getFullYear();
  const [historyYear, setHistoryYear] = useState(currentYear);
  
  // Generate year options from 2024 to current year
  const yearOptions = [];
  for (let y = 2024; y <= currentYear; y++) {
    yearOptions.push(y);
  }

  useEffect(() => {
    fetchAllApprovals();
  }, []);

  useEffect(() => {
    if (mainTab === 'history') {
      fetchApprovalHistory(historyYear);
    }
  }, [mainTab, historyYear]);

  const fetchAllApprovals = async () => {
    try {
      setLoading(true);
      // Fetch all pending declarations (both initial and annual combined)
      const data = await getPendingHodApprovals().catch(e => ({ declarations: [] }));
      setDeclarations(data.declarations || []);
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovalHistory = async (year) => {
    try {
      setHistoryLoading(true);
      const data = await getHodApprovalHistory(year);
      setApprovalHistory(data.approvals || []);
    } catch (error) {
      console.error('Error fetching approval history:', error);
      toast.error('Failed to load approval history');
      setApprovalHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleApprove = async (id, comment = '') => {
    setProcessing(prev => ({ ...prev, [id]: true }));
    
    try {
      await approveAssetDeclaration({
        allocation_id: id,
        approved: 1,
        comment: comment
      });
      setDeclarations(prev => prev.filter(d => d.id !== id));

      toast.success('Declaration approved successfully');
    } catch (error) {
      console.error('Error approving declaration:', error);
      toast.error('Failed to approve declaration');
    } finally {
      setProcessing(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleReject = async (id) => {
    const comment = comments[id] || '';
    
    if (!comment.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }

    setProcessing(prev => ({ ...prev, [id]: true }));
    
    try {
      await approveAssetDeclaration({
        allocation_id: id,
        approved: 2,
        comment: comment
      });
      setDeclarations(prev => prev.filter(d => d.id !== id));

      toast.success('Declaration rejected');
    } catch (error) {
      console.error('Error rejecting declaration:', error);
      toast.error('Failed to reject declaration');
    } finally {
      setProcessing(prev => ({ ...prev, [id]: false }));
    }
  };

  // Toggle selection of a single declaration
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  // Toggle select all declarations
  const toggleSelectAll = () => {
    if (selectedIds.length === declarations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(declarations.map(d => d.id));
    }
  };

  // Handle bulk approval
  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one declaration');
      return;
    }

    try {
      const response = await bulkApproveAssetDeclarations({
        allocation_ids: selectedIds,
        approved: 1,
        comment: bulkComment || 'Bulk approved'
      });

      const data = response.data || response;
      const successCount = data.success_count || 0;
      const failCount = data.fail_count || 0;
      const errors = data.errors || [];
      
      if (successCount > 0) {
        // Refresh declarations list to remove approved ones
        fetchAllApprovals();
        setSelectedIds([]);
        setBulkComment('');
      }
      
      if (failCount > 0 && successCount > 0) {
        toast.warning(`Approved ${successCount} declarations. ${failCount} failed.`);
      } else if (failCount > 0 && successCount === 0) {
        toast.error(`All ${failCount} declarations failed: ${errors.join(', ')}`);
      } else {
        toast.success(`Successfully approved ${successCount} declarations`);
      }
    } catch (error) {
      console.error('Error bulk approving:', error);
      const errorMsg = error.response?.data?.message || 'Failed to bulk approve declarations';
      toast.error(errorMsg);
    }
  };

  // Handle bulk rejection
  const handleBulkReject = async () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one declaration');
      return;
    }

    if (!bulkComment.trim()) {
      toast.warning('Please provide a reason for bulk rejection');
      return;
    }

    try {
      const response = await bulkApproveAssetDeclarations({
        allocation_ids: selectedIds,
        approved: 2,
        comment: bulkComment
      });

      const data = response.data || response;
      const successCount = data.success_count || 0;
      const failCount = data.fail_count || 0;
      const errors = data.errors || [];
      
      if (successCount > 0) {
        // Refresh declarations list to remove rejected ones
        fetchAllApprovals();
        setSelectedIds([]);
        setBulkComment('');
      }
      
      if (failCount > 0 && successCount > 0) {
        toast.warning(`Rejected ${successCount} declarations. ${failCount} failed.`);
      } else if (failCount > 0 && successCount === 0) {
        toast.error(`All ${failCount} declarations failed: ${errors.join(', ')}`);
      } else {
        toast.success(`Successfully rejected ${successCount} declarations`);
      }
    } catch (error) {
      console.error('Error bulk rejecting:', error);
      const errorMsg = error.response?.data?.message || 'Failed to bulk reject declarations';
      toast.error(errorMsg);
    }
  };

  // Format date - handles Unix timestamps and date strings
  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    
    let date;
    if (typeof dateValue === 'number' || /^\d+$/.test(dateValue)) {
      date = new Date(Number(dateValue) * 1000);
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-ZA', { 
      weekday: 'short',
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPending = declarations.length;

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading pending approvals...</p>
        </div>
      </div>
    );
  }

  const renderDeclarationCard = (declaration) => {
    const id = declaration.id;
    const isProcessingCard = processing[id];
    const isSelected = selectedIds.includes(id);
    
    return (
      <div
        key={id}
        className={`bg-white shadow-sm border-2 rounded-lg overflow-hidden ${isSelected ? 'border-green-500 ring-2 ring-green-200' : 'border-blue-500'}`}
      >
        {/* Header */}
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelect(id)}
                className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-3 cursor-pointer"
              />
              <Package className="w-5 h-5 text-blue-600 mr-2" />
              <span className="font-semibold text-blue-800 text-sm">
                Declared on {formatDate(declaration.allocate_declared)}
              </span>
            </div>
            <span className="text-sm bg-blue-100 text-blue-800 font-semibold px-3 py-1 rounded-full">
              {declaration.staff_name}
            </span>
          </div>
        </div>

        {/* Asset Details */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Asset Name</label>
              <p className="text-base font-semibold text-gray-900">
                {declaration.asset_name || 'N/A'}
              </p>
            </div>
            
            {declaration.asset_tag && (
              <div>
                <label className="text-sm font-medium text-gray-500">Asset Tag</label>
                <p className="text-base font-semibold text-gray-900">
                  {declaration.asset_tag}
                </p>
              </div>
            )}
            
            {declaration.asset_serial && (
              <div>
                <label className="text-sm font-medium text-gray-500">Serial Number</label>
                <p className="text-base text-gray-900">
                  {declaration.asset_serial}
                </p>
              </div>
            )}
            
            {declaration.category && (
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <p className="text-base text-gray-900">
                  {declaration.category}
                </p>
              </div>
            )}

            {declaration.vendor && (
              <div>
                <label className="text-sm font-medium text-gray-500">Vendor</label>
                <p className="text-base text-gray-900">
                  {declaration.vendor}
                </p>
              </div>
            )}

            {declaration.asset_cost && (
              <div>
                <label className="text-sm font-medium text-gray-500">Cost</label>
                <p className="text-base text-gray-900">
                  R {parseFloat(declaration.asset_cost).toLocaleString()}
                </p>
              </div>
            )}
          </div>

          {/* Optional Comment */}
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Comment (required for rejection)
            </label>
            <textarea
              value={comments[id] || ''}
              onChange={(e) => setComments(prev => ({ 
                ...prev, 
                [id]: e.target.value 
              }))}
              disabled={isProcessingCard}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
              rows={3}
              placeholder="Enter your comment here..."
            />
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => handleApprove(id, comments[id])}
              disabled={isProcessingCard}
              className="px-4 py-3 bg-green-600 text-white text-base font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {isProcessingCard ? (
                <>
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Approve
                </>
              )}
            </button>

            <button
              onClick={() => handleReject(id)}
              disabled={isProcessingCard}
              className="px-4 py-3 bg-red-600 text-white text-base font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              {isProcessingCard ? (
                <>
                  <Loader className="w-5 h-5 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 mr-2" />
                  Reject
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHistoryCard = (approval) => {
    const isApproved = approval.allocate_hod_approved === 1 || approval.allocate_hod_approved === '1';
    const borderColor = isApproved ? 'border-green-400' : 'border-red-400';
    const statusBg = isApproved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    const statusText = isApproved ? 'Approved' : 'Rejected';
    
    return (
      <div
        key={approval.id}
        className={`bg-white shadow-sm border-l-4 ${borderColor} rounded-lg overflow-hidden`}
      >
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold text-gray-900">{approval.asset_name || 'N/A'}</h3>
              {approval.asset_tag && (
                <p className="text-sm text-gray-500">{approval.asset_tag}</p>
              )}
            </div>
            <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusBg}`}>
              {statusText}
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Staff:</span>
              <p className="font-medium text-gray-900">{approval.staff_name}</p>
            </div>
            <div>
              <span className="text-gray-500">Declared:</span>
              <p className="font-medium text-gray-900">{formatDate(approval.allocate_declared)}</p>
            </div>
            <div>
              <span className="text-gray-500">{isApproved ? 'Approved' : 'Rejected'} by:</span>
              <p className="font-medium text-gray-900">{approval.hod_name || 'N/A'}</p>
            </div>
            <div>
              <span className="text-gray-500">{isApproved ? 'Approved' : 'Rejected'} on:</span>
              <p className="font-medium text-gray-900">{formatDate(approval.allocate_hod_date)}</p>
            </div>
          </div>
          
          {approval.allocate_hod_comment && (
            <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
              <span className="text-gray-500">Comment: </span>
              <span className="text-gray-700">{approval.allocate_hod_comment}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[70vh] bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-orange-600 mr-3 flex-shrink-0 mt-1" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Asset Declaration Approvals
                </h1>
                <p className="text-gray-600">
                  {totalPending > 0 ? (
                    <>
                      You have <span className="font-semibold text-orange-600">{totalPending}</span> declaration{totalPending !== 1 ? 's' : ''} awaiting your approval.
                    </>
                  ) : (
                    'No pending asset declarations require your approval at this time.'
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                fetchAllApprovals();
                if (mainTab === 'history') fetchApprovalHistory(historyYear);
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCcw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Tabs */}
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setMainTab('pending')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                mainTab === 'pending'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              } transition-colors`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              Declaration Approvals
              {totalPending > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-800">
                  {totalPending}
                </span>
              )}
            </button>
            <button
              onClick={() => setMainTab('history')}
              className={`flex-1 px-4 py-3 text-sm font-medium ${
                mainTab === 'history'
                  ? 'text-gray-700 border-b-2 border-gray-700 bg-gray-100'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              } transition-colors`}
            >
              <History className="w-4 h-4 inline mr-2" />
              Approval History
            </button>
          </div>
        </div>

        {/* Pending Tab Content */}
        {mainTab === 'pending' && (
          <div className="space-y-4">
            {declarations.length === 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <p className="text-gray-600">No declarations awaiting approval.</p>
              </div>
            ) : (
              <>
                {/* Bulk Action Bar */}
                <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === declarations.length && declarations.length > 0}
                        onChange={toggleSelectAll}
                        className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500 mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Select All ({selectedIds.length}/{declarations.length})
                      </span>
                    </label>
                    
                    {selectedIds.length > 0 && (
                      <>
                        <div className="flex-1 min-w-[200px] max-w-md">
                          <input
                            type="text"
                            value={bulkComment}
                            onChange={(e) => setBulkComment(e.target.value)}
                            placeholder="Bulk comment (required for rejection)"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <button
                            onClick={handleBulkApprove}
                            className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors flex items-center"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve Selected ({selectedIds.length})
                          </button>
                          
                          <button
                            onClick={handleBulkReject}
                            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors flex items-center"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject Selected ({selectedIds.length})
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {declarations.map(declaration => renderDeclarationCard(declaration))}
              </>
            )}
          </div>
        )}

        {/* History Tab Content */}
        {mainTab === 'history' && (
          <>
            {/* Year Filter */}
            <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Filter by Year:</span>
                <div className="flex gap-2">
                  {yearOptions.map(year => (
                    <button
                      key={year}
                      onClick={() => setHistoryYear(year)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        historyYear === year
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* History List */}
            {historyLoading ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                <p className="text-gray-600">Loading approval history...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {approvalHistory.length === 0 ? (
                  <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8 text-center">
                    <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No approval history found for {historyYear}.</p>
                  </div>
                ) : (
                  <>
                    <div className="text-sm text-gray-500 mb-2">
                      Showing {approvalHistory.length} approval{approvalHistory.length !== 1 ? 's' : ''} for {historyYear}
                    </div>
                    {approvalHistory.map(approval => renderHistoryCard(approval))}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
